/**
 * 在投广告数分国家主组件
 * 对应板块: 板块 E — 在投广告数分国家
 *
 * 布局：左侧世界地图热力图 + 右侧折线/柱状/饼图/表格
 * 交互：
 *   - 默认场景：国家筛选器多选（NewRegionSelect），默认 Top 10（传空 arr 给后端）
 *   - 勾选交叉分析后：国家筛选器变单选，无 Top 10 选项，默认选中 United States
 * 国家筛选器数据来源：api/v1/marketing/filter/cond → materials_config_region_countries
 * 使用 country_code (alpha-2) 作为后端传参
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col, Checkbox } from 'tea-component';
import CardCommon from '@op/components/CardCommon';
import BubbleCommon, { adsActiveAdsTips } from '@op/components/BubbleCommon';
import type { TRequestBackType } from '@op/utils/requestUtil';
import useLanguage from '@op/hooks/useLanguage';
import NewRegionSelect from '@/pages/home/components/NewRegionSelect';
import type { RegionSelectValueType } from '@/pages/home/components/NewRegionSelect/type';
import SingleRegionSelect from './SingleRegionSelect';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { useDimensionData } from '../../hooks/useDimensionData';
import { useTimelineData } from '../../hooks/useTimelineData';
import CountryHeatmap from './CountryHeatmap';
import CountryTrendChart from './CountryTrendChart';
import type {
  TChartType,
  ICrossAnalysisConfig,
  ICountryAnalysisProps,
  ICondRegionItem,
  TCrossMetric,
} from './types';
import {
  CROSS_METRIC_TEXT_MAP,
  getCrossMetricsByEntityType,
  getCountryDisplayName,
  getCountryMapName,
  normalizeCountryHeatmapData,
} from './types';
import './index.scss';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import { useFilterCondData } from '@opm/components/FilterCommon/hooks/useFilterCondData';
import { DBO_TM } from '@op/utils/format';
import { getChartMetricLabelKey } from '../../utils/metricLabels';

/** 默认交叉分析国家代码 (alpha-2, 传给后端) */
const DEFAULT_CROSS_COUNTRY = 'US';

type TCountryFilter = NonNullable<ICountryAnalysisProps['filter']> & {
  need_others: boolean;
};

function getDimensionTopN(isCross: boolean, dimensionValues?: string[]): number | undefined {
  if (isCross) return 500;
  if (dimensionValues) return undefined;
  return 10;
}

const CountryAnalysis: React.FC<ICountryAnalysisProps> = ({ filter, gameInfo }) => {
  const { formatMessage, isEn } = useLanguage();
  const locale = isEn ? 'en-US' : 'zh-CN';
  const [chartType, setChartType] = useState<TChartType>('line');
  const [crossAnalysis, setCrossAnalysis] = useState<ICrossAnalysisConfig>({
    enabled: false,
    country: undefined,
    metric: getCrossMetricsByEntityType(gameInfo?.entity_type)[0],
  });

  // ==================== NewRegionSelect 数据 ====================
  const [regionOptions, setRegionOptions] = useState<any[]>([]);
  const [regionValue, setRegionValue] = useState<RegionSelectValueType>({
    type: 'market',
    regions: [],
    markets: ['top10'],
  });

  // ==================== 交叉模式：单选国家 ====================
  const [singleCountry, setSingleCountry] = useState<string>(DEFAULT_CROSS_COUNTRY);
  // 地图选中的 mapName（受控传给 CountryHeatmap）
  const [selectedMapName, setSelectedMapName] = useState<string>('');

  const { loading: condLoading, condData } = useFilterCondData({
    keys: ['ADCountryRegion'],

  });

  // ==================== cond 接口获取可选国家列表 ====================
  useEffect(() => {
    const regions: ICondRegionItem[] = condData?.materials_config_region_countries || [];
    // 构建 NewRegionSelect options（Region → Countries 层级结构）
    const opts = regions.map((region) => ({
      value: region.region_code,
      text: region.region_en,
      textCN: region.region_zh,
      children: (region.countries || []).map((country) => {
        const alpha2 = country.country_code?.trim().toLowerCase() || '';
        const displayName = getCountryDisplayName(country.country_code, true);
        const displayNameCN = getCountryDisplayName(country.country_code, false);
        return {
          value: country.country_code,
          text: displayName || country.country_en,
          textCN: displayNameCN || country.country_zh,
          icon: getCountryFlag(alpha2),
        };
      }),
    }));
    setRegionOptions(opts);
  }, [condData, isEn]);

  // ==================== 判断是否选中了全部国家 ====================
  const isAllSelected = useMemo(() => {
    const { regions = [] } = regionValue;
    return regions.length > 0 && regions.length === regionOptions.length;
  }, [regionValue]);

  // ==================== 派生 filter：全选时附加 need_others ====================
  const countryFilter = useMemo<TCountryFilter>(() => {
    return {
      ...filter,
      need_others: isAllSelected,
    };
  }, [filter, isAllSelected]);

  // ==================== 传给后端的 dimension_values (country_code alpha-2) ====================
  const selectedDimensionValues = useMemo(() => {
    if (crossAnalysis.enabled) {
      return undefined;
    }
    // 默认多选模式
    const { markets = [] } = regionValue;
    // 如果选了 top10 radio 或者没有选国家，传空
    if (markets.length === 0 || markets.includes('top10')) {
      return undefined;
    }
    // 如果全选了，也传空（由 need_others 控制）
    if (isAllSelected) {
      return undefined;
    }
    return markets;
  }, [crossAnalysis.enabled, regionValue, isAllSelected]);

  // ==================== 维度数据（地图热力图 + 饼图 latest） ====================
  const {
    items: dimensionItems,
    loading: dimensionLoading,
    dataDate: dimensionDataDate,
  } = useDimensionData({
    filter,
    gameInfo,
    metric: 'country_stat',
    dimensionValues: selectedDimensionValues,
    topN: getDimensionTopN(crossAnalysis.enabled, selectedDimensionValues),
  });

  // ==================== 时间线数据（折线/柱状图） ====================
  const timelineDimensionValues = useMemo(() => {
    if (crossAnalysis?.enabled) return [singleCountry];
    return selectedDimensionValues === undefined ? [] : selectedDimensionValues;
  }, [crossAnalysis.enabled, singleCountry, selectedDimensionValues]);

  const {
    chartData: allCountriesChartData,
    loading: timelineLoading,
    pieData,
    rawData
  } = useTimelineData({
    filter: countryFilter,
    gameInfo: gameInfo as any,
    metric: 'country_stat',
    dimensionValues: timelineDimensionValues,
    topN: 10,
    needCompareTimeline: crossAnalysis.enabled,
  });
  // ==================== 热力图数据 ====================
  const heatmapData = useMemo(
    () => normalizeCountryHeatmapData(dimensionItems, isEn),
    [dimensionItems, isEn],
  );

  // ==================== 指标下拉选项 ====================
  const metricOptions = useMemo(() => {
    const metricList = getCrossMetricsByEntityType(gameInfo?.entity_type);
    return metricList.map((value) => ({
      value,
      text: formatMessage(CROSS_METRIC_TEXT_MAP[value]),
    }));
  }, [formatMessage, gameInfo?.entity_type]);

  // ==================== 联动：指标选项变化时自动校正 ====================
  useEffect(() => {
    const nextMetric = metricOptions.some((item) => item.value === crossAnalysis.metric)
      ? crossAnalysis.metric
      : (metricOptions[0]?.value as TCrossMetric | undefined);

    if (nextMetric !== crossAnalysis.metric) {
      setCrossAnalysis((prev) => ({ ...prev, metric: nextMetric }));
    }
  }, [crossAnalysis.metric, metricOptions]);

  // ==================== 右侧图表数据 ====================
  // useTimelineData 已将 compare_timeline 合并进 chartData.series（用 isCompare 标记），
  // 这里按当前选中的 crossAnalysis.metric 过滤掉无关的 compare 系列，再传给 CountryTrendChart
  const displayChartData = useMemo<TRequestBackType>(() => {
    const base = allCountriesChartData || { series: [], xAxis: [], legend: [], isEmpty: true };
    if (!crossAnalysis.enabled || !crossAnalysis.country || !crossAnalysis.metric) {
      // 非交叉模式：剔除所有 compare 系列
      const primarySeries = (base.series || []).filter(
        (s: any) => !s.isCompare,
      );
      return { ...base, series: primarySeries };
    }
    // 交叉模式：仅保留与当前 metric 匹配的 compare 系列
    const metricKey = crossAnalysis.metric;
    const filteredSeries = (base.series || []).filter(
      (s: any) => !s.isCompare || s.name === metricKey,
    );
    return { ...base, series: filteredSeries };
  }, [allCountriesChartData, crossAnalysis.enabled, crossAnalysis.country, crossAnalysis.metric]);

  // 从 compare_timeline 的最后一个时间点提取当前 metric 的最新值
  // crossMetricLatestMap: dimensionValue (country alpha-2) → latest metric value
  const crossMetricLatestMap = useMemo<Record<string, number>>(() => {
    if (!crossAnalysis.enabled || !crossAnalysis.metric || !allCountriesChartData) return {};
    const metricKey = crossAnalysis.metric;
    const metricSeries = (allCountriesChartData.series || []).find(
      (s: any) => s.isCompare && s.name === metricKey,
    );
    if (!metricSeries?.data?.length) return {};
    // 最新值取 series 最后一个数据点
    const latestValue = metricSeries.data[metricSeries.data.length - 1] ?? 0;
    // 热力图 tooltip 按 dimensionValue (country code) 查找
    // 在交叉模式下只有一个国家，用 singleCountry 作 key
    const map: Record<string, number> = {};
    if (singleCountry) {
      map[singleCountry] = latestValue;
    }
    return map;
  }, [crossAnalysis.enabled, crossAnalysis.metric, allCountriesChartData, singleCountry]);

  // ==================== 地图点击 → 联动筛选器 ====================
  const handleCountryClick = useCallback((countryCode: string, mapName: string) => {
    setSelectedMapName(mapName);
    setSingleCountry(countryCode);
    setCrossAnalysis((prev) => ({
      ...prev,
      enabled: true,
      country: countryCode,
      metric: prev.metric || metricOptions[0]?.value as TCrossMetric | undefined,
    }));
    setChartType('line');
  }, [metricOptions]);

  // ==================== NewRegionSelect onChange ====================
  const handleRegionSelectClose = useCallback((val: RegionSelectValueType) => {
    setRegionValue(val);
  }, []);

  // ==================== 交叉分析开关 ====================
  const handleCrossAnalysisChange = useCallback((value: boolean) => {
    if (value) {
      const defaultCountry = DEFAULT_CROSS_COUNTRY;
      const defaultMapName = getCountryMapName(defaultCountry) || '';
      setSingleCountry(defaultCountry);
      handleCountryClick(defaultCountry, defaultMapName);
    } else {
      setCrossAnalysis((prev) => ({ ...prev, enabled: false }));
      setSelectedMapName('');
    }
  }, [handleCountryClick]);

  const trendTitle = useMemo(() => {
    return crossAnalysis.enabled
      ? formatMessage(getChartMetricLabelKey('Dual-Axis Comparison with Active Ads', filter))
      : formatMessage(getChartMetricLabelKey('Active Ads', filter));
  }, [filter, formatMessage]);

  const cardTitle = useMemo(() => {
    const text = formatMessage(getChartMetricLabelKey('Active Ads by Country', filter));
    return <BubbleCommon placement="top" tipsMap={adsActiveAdsTips}>{text}</BubbleCommon>;
  }, [formatMessage, filter]);

  // ==================== NewRegionSelect radio 选项 ====================
  const regionRadioOptions = useMemo(() => [
    { value: 'top10', text: 'Top 10', textCN: 'Top 10' },
  ], []);

  return (
    <CardCommon
      title={cardTitle}
      loading={condLoading}
      className="dbo-country-analysis"
    >
      <div className="dbo-country-analysis__toolbar">
        <Checkbox
          value={crossAnalysis.enabled}
          onChange={(value) => handleCrossAnalysisChange(Boolean(value))}
        >
          {formatMessage('Game Performance Cross-Analysis')}
        </Checkbox>
        {/* 国家筛选器 */}
        {crossAnalysis.enabled ? (
          <SingleRegionSelect
            value={singleCountry}
            options={regionOptions}
            onChange={(val) => {
              setSingleCountry(val);
              handleCountryClick(
                val,
                getCountryMapName(val) || '',
              );
            }}
            locale={locale}
            placeholder={formatMessage('Country')}
          />
        ) : (
          <NewRegionSelect
            value={regionValue}
            radioOptions={regionRadioOptions}
            options={regionOptions}
            onCloseReginSelect={handleRegionSelectClose}
            onChange={handleRegionSelectClose}
            locale={locale}
            placeholder={`${formatMessage('Country')}`}
            shortcutOptions={[]}
            realRegionsList={[]}
          />
        )}

        {
          crossAnalysis.enabled && (
            <SortBySelect
              defaultValue={crossAnalysis.metric}
              onChange={(key: string, value: any) => {
                setCrossAnalysis((prev) => ({ ...prev, metric: value as TCrossMetric }));
              }}
              defaultParams={{
                options: metricOptions,
                needFormatMessage: false,
                btnTextPrefix: formatMessage({ id: 'Metrics' }),
                matchButtonWidth: true,
                dealwithButton: (value: any, { textMap }: any) => {
                  return textMap[value];
                },
              }}
            />
          )
        }
        {crossAnalysis.enabled && (
          <span className="dbo-country-analysis__cross-hint">
            {formatMessage('Dual-Axis Comparison with Active Ads')}
          </span>
        )}
      </div>

      <Row className="dbo-country-analysis__chart-layout">
        <Col span={12} className="dbo-country-analysis__map-col">
          <CountryHeatmap
            data={heatmapData}
            loading={dimensionLoading}
            selectedMapName={selectedMapName}
            onCountryClick={handleCountryClick}
            crossAnalysis={crossAnalysis}
            crossMetricLatestMap={crossMetricLatestMap}
            dataDate={dimensionDataDate}
            dateType={filter?.date_type}
            filter={filter}
          />
        </Col>
        <Col span={12} className="dbo-country-analysis__trend-col">
          <CountryTrendChart
            title={trendTitle}
            data={displayChartData}
            loading={timelineLoading}
            chartType={chartType}
            crossAnalysis={crossAnalysis}
            pieData={pieData}
            pieDataDate={DBO_TM.timeTitle({
              start_time: rawData?.timeline_of_start_time || '',
              end_time: rawData?.timeline_of_end_time || '',
              date_type: filter?.date_type || 'daily',
            })}
            filter={filter}
            setChartType={setChartType}
          />
        </Col>
      </Row>
    </CardCommon>
  );
};

export default React.memo(CountryAnalysis);
